import React, { useRef } from 'react'
import styled from 'styled-components'

export interface TabItem {
  id: string
  label: React.ReactNode
  content: React.ReactNode
}

interface TabsProps {
  items: TabItem[]
  activeIndex: number
  onChange: (index: number) => void
  /** Nome accessibile della tablist: dice a cosa si riferiscono queste schede. */
  ariaLabel: string
  /** Permette a styled(Tabs) di ridefinire l'aspetto dal chiamante. */
  className?: string
}

/**
 * Pattern Tabs WAI-ARIA.
 *
 * Sostituisce i quattro `<Switch onClick isActive>` (styled.div) sparsi nelle
 * pagine: tre non avevano nemmeno tabIndex, quindi erano inoperabili da
 * tastiera; quello di assistenza faceva `onKeyDown={() => setTab(n)}`, che
 * scattava su QUALSIASI tasto — Tab compreso — cambiando scheda da solo.
 * Chiude RCXEK, RF1M2, 695BU.
 */
export default function Tabs({
  items,
  activeIndex,
  onChange,
  ariaLabel,
  className,
}: TabsProps) {
  const tabRefs = useRef<Array<HTMLButtonElement | null>>([])

  // Le frecce spostano fra i tab; Tab esce dalla tablist e va nel pannello.
  const handleKeyDown = (e: React.KeyboardEvent<HTMLButtonElement>) => {
    let next: number | null = null

    if (e.key === 'ArrowRight') next = (activeIndex + 1) % items.length
    else if (e.key === 'ArrowLeft') next = (activeIndex - 1 + items.length) % items.length
    else if (e.key === 'Home') next = 0
    else if (e.key === 'End') next = items.length - 1

    if (next === null) return

    e.preventDefault()
    onChange(next)
    tabRefs.current[next]?.focus()
  }

  return (
    <Wrapper className={className}>
      <TabList role="tablist" aria-label={ariaLabel}>
        {items.map((item, index) => (
          <Tab
            key={item.id}
            ref={(el) => {
              tabRefs.current[index] = el
            }}
            type="button"
            role="tab"
            id={`tab-${item.id}`}
            aria-selected={index === activeIndex}
            /* Posizione nel gruppo: NVDA la annuncia solo se dichiarata. Nella
               verifica D1 del 2026-09-01 le schede dicevano "FAQ, tab,
               selezionato" senza il "1 di 2" che la checklist si aspettava.
               Non e' un requisito di 4.1.2, ma e' informazione che chi ascolta
               non ha altro modo di ricavare. */
            aria-posinset={index + 1}
            aria-setsize={items.length}
            /* Solo la scheda attiva: i pannelli non attivi non sono resi,
               quindi il loro id non esiste. */
            aria-controls={index === activeIndex ? `panel-${item.id}` : undefined}
            tabIndex={index === activeIndex ? 0 : -1}
            $isActive={index === activeIndex}
            onClick={() => onChange(index)}
            onKeyDown={handleKeyDown}
          >
            {item.label}
          </Tab>
        ))}
      </TabList>

      {items.map((item, index) =>
        index === activeIndex ? (
          <TabPanel
            key={item.id}
            role="tabpanel"
            id={`panel-${item.id}`}
            aria-labelledby={`tab-${item.id}`}
            tabIndex={0}
          >
            {item.content}
          </TabPanel>
        ) : null
      )}
    </Wrapper>
  )
}

// region Style
const Wrapper = styled.div`
  width: 100%;
`

const TabList = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 1rem;
  justify-content: center;
`

/* Conserva la pillola gialla del design attuale. */
const Tab = styled.button<{ $isActive: boolean }>`
  display: inline-block;
  min-height: 4.6rem;
  padding: 1rem 3rem;
  border-radius: 2.3rem;
  font-family: inherit;
  font-size: 20px;
  font-weight: 700;
  /* #222 su #fcbb00 = 9.28:1, su bianco = 15.9:1 */
  color: #222222;
  cursor: pointer;
  transition: all 0.1s ease-out;
  background-color: ${({ $isActive }) => ($isActive ? '#fcbb00' : 'transparent')};
  /* Lo stato attivo non e' veicolato dal solo colore (WCAG 1.4.1):
     il bordo resta trasparente da inattivo, cosi' non c'e' salto di layout. */
  border: 2px solid ${({ $isActive }) => ($isActive ? '#222222' : 'transparent')};

  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }

  &:focus {
    outline: none;
  }
  &:focus-visible {
    outline: 3px solid #005fcc;
    outline-offset: 2px;
  }
`

const TabPanel = styled.div`
  &:focus {
    outline: none;
  }
  &:focus-visible {
    outline: 3px solid #005fcc;
    outline-offset: 2px;
  }
`
// endregion
